'use client';

import React, { Suspense } from 'react';
import classNames from 'classnames';
import { NoFirstRender } from '@benzinga/hooks';
import { isDesktop as isDesktopViewport, isMobile as isMobileViewport } from '@benzinga/device-utils';
import {
  AD_RESERVED_CLASS,
  allocateFreestarPlacement,
  FREESTAR_PUBLISHER,
  freestarPlacementHeightMap,
  freestarPlacementWidthClass,
  isAdLightAllowedPlacement,
  neutralizeFreestarStickyRailForReactSlots,
  resolveFreestarChannel,
  type FreestarPlacement,
} from '@benzinga/ads-utils';
import { useBenzingaEdge } from '@benzinga/edge';
import { ErrorBoundary } from '@benzinga/core-ui';
import { loadFreestarAdSlot } from './loadFreestarAdSlot';
import { useFreestarPathname } from './useFreestarPathname';

const FreestarAdSlot = React.lazy(() => loadFreestarAdSlot().then(module => ({ default: module.default })));

export type FreestarAdProps = {
  className?: string;
  disableMargins?: boolean;
  minimumHeightEnabled?: boolean;
  onlyDesktop?: boolean;
  onlyMobile?: boolean;
  placement: FreestarPlacement;
  targeting?: Record<string, string | string[] | number | boolean>;
};

export const FreestarAd: React.FC<FreestarAdProps> = ({
  className,
  disableMargins = false,
  minimumHeightEnabled = true,
  onlyDesktop,
  onlyMobile,
  placement,
  targeting,
}) => {
  const { adLightEnabled, disabled, isLoading } = useBenzingaEdge();

  const heightClassnames =
    minimumHeightEnabled && freestarPlacementHeightMap[placement] ? freestarPlacementHeightMap[placement] : '';
  const isMobileClassName = 'flex md:hidden' + (disableMargins ? '' : ' my-2 md:my-0');
  const isDesktopClassName = 'hidden md:flex' + (disableMargins ? '' : ' md:my-2');
  const deviceBasedClassnames = onlyMobile ? isMobileClassName : onlyDesktop ? isDesktopClassName : '';
  const widthClassnames = freestarPlacementWidthClass(placement, className);
  const reservedBoxClassnames = classNames(
    AD_RESERVED_CLASS,
    widthClassnames,
    'shrink-0',
    deviceBasedClassnames,
    className,
    heightClassnames,
  );
  const placementClassnames = classNames(
    AD_RESERVED_CLASS,
    widthClassnames,
    'freestar-ad-placement mx-auto shrink-0',
    placement === 'sidebar_2' ? 'overflow-visible' : 'overflow-hidden',
    deviceBasedClassnames,
    className,
    heightClassnames,
  );
  const placeholder = <div className={placementClassnames} data-placement={placement} />;
  const allowDuringAdLight = isAdLightAllowedPlacement(placement);
  if (adLightEnabled && !allowDuringAdLight) return null;
  if (!allowDuringAdLight && (isLoading || disabled.displayAd)) {
    return <div className={reservedBoxClassnames} data-placement={placement} />;
  }

  return (
    <NoFirstRender fallback={placeholder}>
      <FreestarAdDeviceGate
        className={className}
        disableMargins={disableMargins}
        minimumHeightEnabled={minimumHeightEnabled}
        onlyDesktop={onlyDesktop}
        onlyMobile={onlyMobile}
        placement={placement}
        targeting={targeting}
      />
    </NoFirstRender>
  );
};

const FreestarAdDeviceGate: React.FC<FreestarAdProps> = ({
  className,
  disableMargins = false,
  minimumHeightEnabled = true,
  onlyDesktop,
  onlyMobile,
  placement,
  targeting,
}) => {
  const [isMobile, setIsMobile] = React.useState(false);
  const [isDesktop, setIsDesktop] = React.useState(false);
  const [isClient, setIsClient] = React.useState(false);

  React.useLayoutEffect(() => {
    setIsMobile(isMobileViewport());
    setIsDesktop(isDesktopViewport());
    setIsClient(true);
  }, []);

  const heightClassnames =
    minimumHeightEnabled && freestarPlacementHeightMap[placement] ? freestarPlacementHeightMap[placement] : '';
  const isMobileClassName = 'flex md:hidden' + (disableMargins ? '' : ' my-2 md:my-0');
  const isDesktopClassName = 'hidden md:flex' + (disableMargins ? '' : ' md:my-2');
  const deviceBasedClassnames = onlyMobile ? isMobileClassName : onlyDesktop ? isDesktopClassName : '';

  // Wait for viewport before allocating slots — avoids pending dual-header auctions.
  if (!isClient) {
    return (
      <div
        className={classNames(
          AD_RESERVED_CLASS,
          freestarPlacementWidthClass(placement, className),
          'freestar-ad-placement mx-auto shrink-0',
          placement === 'sidebar_2' ? 'overflow-visible' : 'overflow-hidden',
          deviceBasedClassnames,
          className,
          heightClassnames,
        )}
        data-placement={placement}
      />
    );
  }

  if ((onlyMobile && isDesktop) || (onlyDesktop && isMobile)) {
    return null;
  }

  return (
    <FreestarAdUnit
      className={className}
      disableMargins={disableMargins}
      minimumHeightEnabled={minimumHeightEnabled}
      onlyDesktop={onlyDesktop}
      onlyMobile={onlyMobile}
      placement={placement}
      targeting={targeting}
    />
  );
};

const FreestarAdUnit: React.FC<FreestarAdProps> = ({
  className,
  disableMargins = false,
  minimumHeightEnabled = true,
  onlyDesktop,
  onlyMobile,
  placement,
  targeting,
}) => {
  const pathname = useFreestarPathname();
  const channel = resolveFreestarChannel(pathname);
  const [allocation] = React.useState(() => allocateFreestarPlacement(placement));
  const needsSidebar1Gate = placement === 'sidebar_1';
  const [sidebar1Ready, setSidebar1Ready] = React.useState(() => !needsSidebar1Gate);

  React.useEffect(() => {
    if (!needsSidebar1Gate) return;

    neutralizeFreestarStickyRailForReactSlots();
    const freestar = (window.freestar ??= {});
    const queue = (freestar.queue ??= []);

    if (freestar.fsdata?.placements?.length) {
      setSidebar1Ready(true);
      return;
    }

    queue.push(() => {
      setSidebar1Ready(true);
    });
  }, [needsSidebar1Gate]);

  if (!allocation) return null;

  const heightClassnames =
    minimumHeightEnabled && freestarPlacementHeightMap[placement] ? freestarPlacementHeightMap[placement] : '';
  const isMobileClassName = 'flex md:hidden' + (disableMargins ? '' : ' my-2 md:my-0');
  const isDesktopClassName = 'hidden md:flex' + (disableMargins ? '' : ' md:my-2');
  const deviceBasedClassnames = onlyMobile ? isMobileClassName : onlyDesktop ? isDesktopClassName : '';
  const allowOverflow = placement === 'sidebar_2';

  return (
    <ErrorBoundary name="freestar-ad">
      <div
        className={classNames(
          AD_RESERVED_CLASS,
          'freestar-ad-placement mx-auto shrink-0 flex justify-center items-start text-center',
          freestarPlacementWidthClass(placement, className),
          allowOverflow ? 'overflow-visible' : 'overflow-hidden',
          '[&>div]:mx-auto [&>div]:flex [&>div]:w-full [&>div]:justify-center [&>div]:min-h-full',
          '[&_iframe]:mx-auto [&_[id*="google_ads"]]:mx-auto',
          deviceBasedClassnames,
          className,
          heightClassnames,
        )}
        data-freestar-placement={allocation.placementName}
        data-placement={placement}
      >
        {needsSidebar1Gate && !sidebar1Ready ? (
          <div
            className={classNames(AD_RESERVED_CLASS, 'mx-auto w-full', heightClassnames)}
            data-placement={placement}
          />
        ) : (
          <Suspense
            fallback={
              <div
                className={classNames(AD_RESERVED_CLASS, 'mx-auto w-full', heightClassnames)}
                data-placement={placement}
              />
            }
          >
            <FreestarAdSlot
              channel={channel}
              classList={['flex', 'justify-center', 'items-start', 'mx-auto', 'w-full', 'min-h-full']}
              placementName={allocation.placementName}
              publisher={FREESTAR_PUBLISHER}
              slotId={allocation.slotId}
              targeting={targeting}
            />
          </Suspense>
        )}
      </div>
    </ErrorBoundary>
  );
};
